import React, { useState, useEffect } from 'react';
import { FiX, FiSearch, FiTruck, FiLoader, FiEdit2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { suppliersAPI } from '../../services/api';

const AddSupplierModal = ({ isOpen, onClose, onSupplierSaved, supplier = null }) => {
  const isEditMode = Boolean(supplier?.id);

  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    gst_number: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [loading, setLoading] = useState(false);
  const [gstinLoading, setGstinLoading] = useState(false);
  const [gstinChecked, setGstinChecked] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens/closes or supplier changes
  useEffect(() => {
    if (isOpen) {
      if (supplier) {
        // Edit mode - pre-fill with supplier data
        setFormData({
          name: supplier.name || '',
          company_name: supplier.company_name || '',
          email: supplier.email || '',
          phone: supplier.phone || '',
          gst_number: supplier.gst_number || '',
          address: supplier.address || '',
          city: supplier.city || '',
          state: supplier.state || '',
          pincode: supplier.pincode || ''
        });
        setGstinChecked(Boolean(supplier.gst_number));
      } else {
        // Add mode - reset form
        setFormData({
          name: '',
          company_name: '',
          email: '',
          phone: '',
          gst_number: '',
          address: '',
          city: '',
          state: '',
          pincode: ''
        });
        setGstinChecked(false);
      }
      setErrors({});
    }
  }, [isOpen, supplier]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Reset GSTIN check when GSTIN changes
    if (name === 'gst_number') {
      setGstinChecked(false);
    }
  };

  const handleGstinLookup = async () => {
    const gstin = formData.gst_number.trim().toUpperCase();

    if (!gstin) {
      toast.error('Please enter a GSTIN');
      return;
    }

    // Basic format validation
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstinRegex.test(gstin)) {
      setErrors(prev => ({ ...prev, gst_number: 'Invalid GSTIN format (e.g., 22AAAAA0000A1Z5)' }));
      return;
    }

    setGstinLoading(true);
    try {
      const response = await suppliersAPI.lookupGstin(gstin);

      if (response.data.exists) {
        // In edit mode, allow if it's the same supplier's GSTIN
        if (isEditMode && response.data.data?.supplierId === supplier.id) {
          setFormData(prev => ({
            ...prev,
            gst_number: gstin,
            state: response.data.data.state || prev.state
          }));
          setGstinChecked(true);
          toast.success('GSTIN verified successfully');
        } else {
          toast.warning('Supplier with this GSTIN already exists');
          setErrors(prev => ({ ...prev, gst_number: 'GSTIN already exists in database' }));
        }
      } else {
        // Auto-fill state from GSTIN
        setFormData(prev => ({
          ...prev,
          gst_number: gstin,
          state: response.data.data.state || prev.state
        }));
        setGstinChecked(true);
        toast.success('GSTIN verified successfully');
      }
    } catch (error) {
      console.error('GSTIN lookup error:', error);
      if (error.response?.data?.message) {
        setErrors(prev => ({ ...prev, gst_number: error.response.data.message }));
      }
    } finally {
      setGstinLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Supplier name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.phone && !/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone should be 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let response;

      if (isEditMode) {
        // Update existing supplier
        response = await suppliersAPI.update(supplier.id, formData);
        if (response.data.status === 'success') {
          toast.success('Supplier updated successfully');
          // Return updated supplier data with id
          onSupplierSaved(response.data.data || { ...formData, id: supplier.id });
          onClose();
        }
      } else {
        // Create new supplier
        response = await suppliersAPI.create(formData);
        if (response.data.status === 'success') {
          toast.success('Supplier created successfully');
          onSupplierSaved(response.data.data);
          onClose();
        }
      }
    } catch (error) {
      console.error(isEditMode ? 'Update supplier error:' : 'Create supplier error:', error);
      // Error toast is handled by API interceptor
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isEditMode ? 'bg-orange-100' : 'bg-green-100'}`}>
              {isEditMode ? (
                <FiEdit2 className="text-orange-600" size={20} />
              ) : (
                <FiTruck className="text-green-600" size={20} />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {isEditMode ? 'Edit Supplier' : 'Add New Supplier'}
              </h2>
              <p className="text-sm text-gray-500">
                {isEditMode ? 'Update supplier details' : 'Enter supplier details'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* GSTIN Field with Lookup */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GSTIN
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="gst_number"
                  value={formData.gst_number}
                  onChange={handleInputChange}
                  placeholder="e.g., 22AAAAA0000A1Z5"
                  maxLength={15}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent uppercase ${
                    errors.gst_number ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={handleGstinLookup}
                  disabled={gstinLoading || !formData.gst_number.trim()}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  {gstinLoading ? (
                    <FiLoader className="animate-spin" size={16} />
                  ) : (
                    <FiSearch size={16} />
                  )}
                  Verify
                </button>
              </div>
              {errors.gst_number && (
                <p className="mt-1 text-sm text-red-500">{errors.gst_number}</p>
              )}
              {gstinChecked && !errors.gst_number && (
                <p className="mt-1 text-sm text-green-600">GSTIN verified</p>
              )}
            </div>

            {/* Supplier Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter supplier name"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                placeholder="Enter company name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Email and Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="supplier@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="2"
                placeholder="Enter address"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* City, State, Pincode */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter state"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pincode
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  placeholder="Enter pincode"
                  maxLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 ${
                isEditMode ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {loading ? (
                <>
                  <FiLoader className="animate-spin" size={16} />
                  {isEditMode ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                isEditMode ? 'Update Supplier' : 'Save Supplier'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSupplierModal;
